import React from 'react';
import { 
  Menu, X, Truck, AlertTriangle, Clock, Coffee, Zap, Moon, Bell, Shield, 
  MapPin, BedDouble, Check, Volume2, VolumeX, Info, FileText, Home, 
  ChevronDown, LogOut, Fuel, Gauge, ClipboardList, BarChart3, Settings, Eye, UserPlus
} from 'lucide-react';
import { formatTime } from '../../utils/formatters';
import PushToggle from '../admin/dashboard/PushToggle';
import NotificationBell from '../shared/NotificationBell';

// Driver Header Component
export const DriverHeader = ({ 
  user, 
  vehicleInfo, 
  isBusinessRegime, 
  getClockTime, 
  getClockDate, 
  useBaseTime, 
  onToggleTime, 
  onShowVehicleModal, 
  onShowMenu,
  onOpenFleetInvites,
}) => (
  <header className={`backdrop-blur-lg border-b px-2 sm:px-3 py-2 sticky top-0 z-40 ${
    vehicleInfo 
      ? (isBusinessRegime ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-blue-900/30 border-blue-700/50')
      : 'bg-slate-800/80 border-slate-700'
  }`}>
    <div className="flex items-center justify-between">
      {/* LEFT: LAWD + Driver Name */}
      <div className="flex items-center gap-1.5 sm:gap-2 min-w-[80px] sm:min-w-[100px]">
        <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isBusinessRegime ? 'bg-emerald-500' : 'bg-blue-500'
        }`}>
          <Truck className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <h1 className="text-white font-semibold text-xs sm:text-sm">LAWD</h1>
          <p className="text-[10px] sm:text-xs text-slate-400 truncate">{user.first_name}</p>
        </div>
      </div>
      
      {/* CENTER: Vehicle Info */}
      <div 
        className="flex-1 mx-2 sm:mx-3 text-center cursor-pointer min-w-0"
        onClick={onShowVehicleModal}
        data-testid="vehicle-banner"
      >
        {vehicleInfo ? (
          <div>
            <p className="text-white font-medium text-xs sm:text-sm truncate">{vehicleInfo.rego}</p>
            <p className="text-[10px] sm:text-xs text-slate-300">{vehicleInfo.gvm_kg?.toLocaleString() || '—'} kg</p>
            <p className={`text-[10px] sm:text-xs font-medium ${isBusinessRegime ? 'text-emerald-400' : 'text-blue-400'}`}>
              {isBusinessRegime ? 'Business Rules' : 'FRHV Compliance'}
            </p>
          </div>
        ) : (
          <div className="py-1">
            <p className="text-amber-400 text-xs sm:text-sm font-medium flex items-center justify-center gap-1 sm:gap-2">
              <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="truncate">Select vehicle</span>
            </p>
          </div>
        )}
      </div>
      
      {/* RIGHT: Clock + Menu */}
      <div className="flex items-center gap-1.5 sm:gap-2 min-w-[80px] sm:min-w-[120px] justify-end">
        <div className="text-right">
          <p className="text-xs sm:text-base font-mono font-bold text-white" data-testid="server-clock">
            {getClockTime()}
          </p>
          <p className="text-[10px] sm:text-xs text-slate-400">{getClockDate()}</p>
          <button 
            onClick={onToggleTime}
            className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded bg-slate-700/50 hover:bg-slate-600 transition"
            data-testid="time-toggle-btn"
          >
            <span className={useBaseTime ? 'text-emerald-400 font-medium' : 'text-slate-500'}>Base</span>
            <span className="text-slate-600 mx-0.5">/</span>
            <span className={!useBaseTime ? 'text-emerald-400 font-medium' : 'text-slate-500'}>Local</span>
          </button>
        </div>
        <PushToggle />
        <NotificationBell onOpenFleetInvites={onOpenFleetInvites} />
        <button onClick={onShowMenu} className="p-1 sm:p-1.5 text-slate-400 hover:text-white">
          <Menu className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  </header>
);

// Menu Panel Component
export const MenuPanel = ({
  showMenu,
  onClose,
  expandedInfoSection,
  onToggleInfoSection,
  voiceMuted,
  onToggleVoice,
  onShowVehicleModal,
  onShowBaseAddress,
  baseAddress,
  onShowAbout,
  onShowExport,
  onLogout,
  isOwnerDriver = false,
  onShowMyVehicle,
  onShowFuelStop,
  isNonFRHV = false,
  onShowRecordKeeper,
  onShowReports,
  onShowConsent,
  onShowFleetInvites,
  fleetInviteCount = 0,
}) => {
  if (!showMenu) return null;

  const infoSections = [
    { id: 'radius', label: 'Local Rule (100km radius)', content: (
      <>
        <p>FRHV operation is subject to a 100 km radius limitation.</p>
        <p className="font-medium text-slate-300">The system:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>May provide directional assistance</li>
          <li>May provide distance calculation tools</li>
          <li>Does NOT guarantee geofence compliance accuracy</li>
        </ul>
        <p className="font-medium text-slate-300">The Driver:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>MUST verify they remain within 100 km radius when operating under FRHV</li>
          <li>MUST switch to NDWD requirements if exceeding 100 km</li>
        </ul>
        <p className="text-amber-400 mt-2">Failure to comply with National Driver Work Diary requirements remains the responsibility of the Driver.</p>
      </>
    )},
    { id: 'primary', label: 'Primary Prod (160km radius)', content: (
      <>
        <p className="font-medium text-emerald-400 mb-2">Primary Production (160 km radius) applies if transporting:</p>
        
        <p className="font-medium text-slate-300 mt-2">Primary Produce:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
          <li>foodstuff intended for human consumption, including:
            <ul className="list-disc list-inside ml-3 mt-1 space-y-0.5">
              <li>grain, fruit, fungi and vegetables</li>
              <li>live animals grown or bred for food</li>
              <li>apiary products</li>
              <li>eggs, including fish roe</li>
              <li>dairy products</li>
            </ul>
          </li>
          <li>feed intended for animal consumption</li>
          <li>feathers and hides for the production of goods</li>
          <li>derivatives from animals or plants used in medicines or cosmetics</li>
          <li>fibrous by-products of plants and animals used for textiles</li>
        </ul>
        
        <p className="font-medium text-slate-300 mt-3">From a production or accumulation of primary produce:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
          <li>a farm, such as a crop or dairy farm</li>
          <li>a feedlot or paddock</li>
          <li>a silo or picking shed</li>
          <li>an apiary</li>
        </ul>
        
        <p className="font-medium text-slate-300 mt-3">To:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
          <li>another primary production facility, OR</li>
          <li>a point of distribution, sale or processing</li>
        </ul>
        
        <p className="text-amber-400 mt-2 text-xs">AND a direct return journey back to the original primary production facility.</p>
        <p className="text-emerald-400 mt-1 text-xs">OR driving unloaded to primary production facilities.</p>
      </>
    )},
    { id: 'dual', label: 'Dual Diary Requirement', content: (
      <>
        <p className="text-slate-300">If in a day you will drive beyond a radius of:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
          <li>Over 100 km <span className="text-slate-400">(local)</span></li>
          <li>Under 160 km <span className="text-emerald-400">(primary production)</span></li>
        </ul>
        <p className="text-amber-400 font-medium mt-3">you MUST use the NDWD for the entire day</p>
        <p className="font-medium text-slate-300 mt-3">Where NDWD is required:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>The Driver MUST maintain the NDWD</li>
          <li>LAWD does NOT replace the NDWD</li>
        </ul>
      </>
    )},
    { id: 'vehicle', label: 'Vehicle Selection', content: (
      <>
        <p>The system relies on nominated GVM/GCM to determine the fatigue rules that apply.</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Ensure the correct GVM/GCM is provided</li>
          <li>Use GCM if the vehicle will be used with a trailer, even if unladen</li>
          <li>The nominated base is used for determining the 100 km / 160 km radius</li>
        </ul>
      </>
    )},
    { id: 'nwd', label: 'NDWD', content: (
      <>
        <p>The National Driver Work Diary (NDWD) must always be present in the vehicle.</p>
        <p>Where the service is not working the driver must use either the NDWD or keep a record of the work/rest in writing.</p>
        <p className="text-amber-400 mt-2">When travelling outside the 100 km / 160 km radius the NDWD must be completed.</p>
      </>
    )},
    { id: 'workrest', label: 'Work and Rest', content: (
      <>
        <p className="font-medium text-slate-300">For Work under FRHV, time includes:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>driving</li>
          <li>occupying the driver's seat while engine is running (but not NSW)</li>
          <li>instructing or supervising another driver</li>
          <li>performing tasks related to the vehicle</li>
        </ul>
        <p className="font-medium text-slate-300 mt-3">For Rest under FRHV:</p>
        <p>Time not doing FRHV Work.</p>
      </>
    )},
    { id: 'towing', label: 'Towing (GCM Rules)', content: (
      <>
        <p className="font-medium text-orange-400 mb-2">Towing? You need to apply the GCM</p>
        
        <p className="text-slate-300 mb-2">When towing a trailer, caravan, or another vehicle, the <span className="text-white font-medium">Gross Combination Mass (GCM)</span> applies instead of GVM, EVEN when unloaded.</p>
        
        <p className="font-medium text-slate-300 mt-3">This may mean:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
          <li>A <span className="text-blue-400">Light Vehicle (LV)</span> becomes a <span className="text-amber-400">Heavy Vehicle (HV)</span></li>
          <li>A <span className="text-amber-400">Heavy Vehicle (HV)</span> becomes a <span className="text-red-400">Fatigue Regulated Heavy Vehicle (FRHV)</span></li>
        </ul>
        
        <p className="font-medium text-slate-300 mt-3">Examples:</p>
        <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
          <li>4.2t GVM ute + 6t trailer = 10t GCM → <span className="text-amber-400">HV rules apply</span></li>
          <li>11t GVM truck + 3t trailer = 14t GCM → <span className="text-red-400">FRHV rules apply</span></li>
        </ul>
        
        <p className="text-orange-400 mt-3 text-xs font-medium">When towing, you may be required to meet FRHV requirements including Work and Rest limits.</p>
        
        <p className="text-slate-300 mt-2 text-xs">Enable "Towing Mode" when starting work if you are towing.</p>
      </>
    )}
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose}>
      <div 
        className="absolute right-0 top-0 h-full w-72 sm:w-80 bg-slate-800 border-l border-slate-700 overflow-y-auto" 
        onClick={e => e.stopPropagation()}
      >
        <div className="p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-white font-semibold text-sm sm:text-base">Menu</h2>
            <button onClick={onClose} className="text-slate-400">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Export PDF at top */}
          <button 
            onClick={onShowExport}
            className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-slate-300 hover:bg-slate-700 rounded-xl mb-2 text-sm sm:text-base"
            data-testid="export-pdf-menu-btn"
          >
            <FileText className="w-5 h-5" />
            Export PDF
          </button>
          
          <div className="border-b border-slate-700 my-2 sm:my-3" />
          
          {/* Collapsible Info Sections */}
          <div className="space-y-1.5 sm:space-y-2 mb-2 sm:mb-3">
            {infoSections.map(section => (
              <div key={section.id} className="rounded-xl border border-slate-700">
                <button
                  onClick={() => onToggleInfoSection(expandedInfoSection === section.id ? null : section.id)}
                  className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 text-slate-300 hover:bg-slate-700/50 rounded-xl text-sm sm:text-base"
                >
                  <span className="font-medium">{section.label}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${expandedInfoSection === section.id ? 'rotate-180' : ''}`} />
                </button>
                {expandedInfoSection === section.id && (
                  <div className="px-3 sm:px-4 pb-3 text-[10px] sm:text-xs text-slate-400 space-y-2">
                    {section.content}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="border-b border-slate-700 my-2 sm:my-3" />
          
          <div className="space-y-1.5 sm:space-y-2">
            {/* Voice Alerts toggle - disabled, kept for future use */}
            
            {/* My Vehicle - for owner/drivers only */}
            {isOwnerDriver && onShowMyVehicle && (
              <button 
                onClick={onShowMyVehicle}
                className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-emerald-400 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
                data-testid="my-vehicle-menu-btn"
              >
                <Truck className="w-5 h-5" />
                My Vehicle
              </button>
            )}
            
            {/* Select Vehicle - for fleet drivers */}
            {!isOwnerDriver && (
              <button 
                onClick={onShowVehicleModal}
                className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-slate-300 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
                data-testid="select-vehicle-menu-btn"
              >
                <Truck className="w-5 h-5" />
                Select Vehicle
              </button>
            )}
            
            {/* Fuel Stop - for non-FRHV vehicles */}
            {isNonFRHV && onShowFuelStop && (
              <button 
                onClick={onShowFuelStop}
                className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-amber-400 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
                data-testid="fuel-stop-menu-btn"
              >
                <Fuel className="w-5 h-5" />
                Fuel Stop
              </button>
            )}
            
            {/* Driver Diary & Admin Functions */}
            <>
              <div className="border-b border-slate-700 my-2" />
              <div className="px-3 py-1">
                <span className="text-xs text-slate-500 uppercase tracking-wider">{isOwnerDriver ? 'Admin Functions' : 'My Records'}</span>
              </div>
              
              {onShowRecordKeeper && (
                <button 
                  onClick={onShowRecordKeeper}
                  className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-blue-400 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
                  data-testid="record-keeper-menu-btn"
                >
                  <ClipboardList className="w-5 h-5" />
                  My Diary (Edit Entries)
                </button>
              )}
              
              {onShowReports && (
                <button 
                  onClick={onShowReports}
                  className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-purple-400 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
                  data-testid="reports-menu-btn"
                >
                  <BarChart3 className="w-5 h-5" />
                  Reports & Compliance
                </button>
              )}
            </>
            
            <div className="border-b border-slate-700 my-2" />

            {onShowConsent && (
              <button 
                onClick={onShowConsent}
                className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-violet-400 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
                data-testid="consent-menu-btn"
              >
                <Eye className="w-5 h-5" />
                Group View Sharing
              </button>
            )}

            {onShowFleetInvites && (
              <button 
                onClick={onShowFleetInvites}
                className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-emerald-400 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
                data-testid="fleet-invites-menu-btn"
              >
                <UserPlus className="w-5 h-5" />
                Fleet Invites
                {fleetInviteCount > 0 && (
                  <span className="ml-auto bg-emerald-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{fleetInviteCount}</span>
                )}
              </button>
            )}
            
            <button 
              onClick={onShowBaseAddress}
              className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-slate-300 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
              data-testid="base-address-menu-btn"
            >
              <Home className="w-5 h-5" />
              Base Address
            </button>
            <button 
              onClick={onShowAbout}
              className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-slate-300 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
              data-testid="service-menu-btn"
            >
              <Info className="w-5 h-5" />
              Service
            </button>
            <div className="border-b border-slate-700 my-2" />
            <button 
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-red-400 hover:bg-slate-700 rounded-xl text-sm sm:text-base"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Card Component
export const StatusCard = ({ 
  isWorking, 
  isResting, 
  isNightRest, 
  isDayOffSource, 
  elapsedTime,
  workRestartAt,
  workRestartRemainingSec,
  canStartWork,
  formatDisplayTime
}) => {
  const isExtendedRest = isNightRest || isDayOffSource;
  const statusText = isWorking ? 'WORKING' : 
                    isDayOffSource ? 'DAY OFF' :
                    isNightRest ? 'NIGHT REST' : 
                    'RESTING';

  return (
    <div className={`rounded-2xl p-4 sm:p-6 ${
      isWorking ? 'bg-gradient-to-br from-blue-600 to-blue-800' :
      isExtendedRest ? 'bg-gradient-to-br from-slate-600 to-slate-800' :
      'bg-gradient-to-br from-emerald-600 to-emerald-800'
    }`}>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div>
          <p className="text-white/70 text-xs sm:text-sm">Current Status</p>
          <p className="text-xl sm:text-2xl font-bold text-white">{statusText}</p>
        </div>
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center bg-white/20">
          {isWorking ? <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-white" /> :
           isExtendedRest ? <BedDouble className="w-6 h-6 sm:w-8 sm:h-8 text-white" /> :
           <Coffee className="w-6 h-6 sm:w-8 sm:h-8 text-white" />}
        </div>
      </div>
      
      <div className="bg-black/20 rounded-xl p-3 sm:p-4">
        <p className="text-white/70 text-xs sm:text-sm mb-1">Elapsed Time</p>
        <p className="text-2xl sm:text-3xl font-mono font-bold text-white">
          {(() => {
            const safeElapsed = Math.max(0, elapsedTime);
            const hours = Math.floor(safeElapsed / 3600);
            const mins = Math.floor((safeElapsed % 3600) / 60);
            const secs = safeElapsed % 60;
            return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
          })()}
        </p>
      </div>
      
      {isExtendedRest && (
        <div className="mt-3 sm:mt-4 bg-black/20 rounded-xl p-3 sm:p-4">
          <p className="text-white/70 text-xs sm:text-sm mb-1">
            {canStartWork ? 'Work can restart' : 'Work can restart at'}
          </p>
          {canStartWork ? (
            <p className="text-base sm:text-lg font-bold text-emerald-400">Ready to start work</p>
          ) : (
            <>
              <p className="text-lg sm:text-xl font-mono font-bold text-amber-400">
                {workRestartAt ? formatDisplayTime(workRestartAt) : '--:--'}
              </p>
              <p className="text-xs sm:text-sm text-white/50 mt-1">
                {Math.floor(workRestartRemainingSec / 3600)}h {Math.floor((workRestartRemainingSec % 3600) / 60)}m remaining
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Rest Status Card Component
export const RestStatusCard = ({ 
  isResting, 
  isBusinessRegime, 
  driverState, 
  elapsedTime, 
  isUrgent, 
  isWarning, 
  currentSegment,
  complianceStatus,
  formatAbsoluteTime
}) => {
  if (isResting) {
    const canStartWork = isBusinessRegime 
      ? elapsedTime >= (driverState?.rest_requirement_seconds || 1200)
      : ((driverState?.R_credits_24 || 0) >= 4 || elapsedTime >= 900);
    
    return (
      <div className={`rounded-2xl p-3 sm:p-4 bg-emerald-500/20 border border-emerald-500/50`}>
        <div className="relative">
          {/* Start Work indicator */}
          <span className={`absolute top-0 right-0 flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
            canStartWork ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'
          }`}>
            {canStartWork ? <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> : <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />}
            Start
          </span>
          
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
            <Coffee className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
            <p className="text-[10px] sm:text-xs text-emerald-400">
              {isBusinessRegime ? "Rest Status" : (driverState?.R_credits_24 || 0) >= 4 ? "Rest Status" : "Time Before Work"}
            </p>
          </div>
          <p className="text-lg sm:text-xl font-bold text-white">
            {isBusinessRegime ? (
              elapsedTime >= (driverState?.rest_requirement_seconds || 1200) ? (
                <span className="text-emerald-400">Ready</span>
              ) : (
                formatTime(Math.max(0, (driverState?.rest_requirement_seconds || 1200) - elapsedTime))
              )
            ) : (
              (driverState?.R_credits_24 || 0) >= 4 ? (
                <span className="text-emerald-400">Ready</span>
              ) : elapsedTime >= 900 ? (
                <span className="text-emerald-400">Ready</span>
              ) : (
                formatTime(Math.max(0, 900 - elapsedTime))
              )
            )}
          </p>
          <p className="text-[10px] sm:text-xs text-slate-400 mt-1">
            {isBusinessRegime
              ? `Min: ${driverState?.rest_requirement_display || '20m'}`
              : `Current: ${formatTime(elapsedTime)}`}
          </p>
        </div>
      </div>
    );
  }

  // Working or idle state
  return (
    <div className={`rounded-2xl p-3 sm:p-4 ${
      (isBusinessRegime ? driverState?.warning_active : isUrgent) ? 'bg-red-500/20 border border-red-500/50' :
      (isBusinessRegime ? false : isWarning) ? 'bg-amber-500/20 border border-amber-500/50' :
      'bg-slate-800 border border-slate-700'
    }`}>
      <div className="relative">
        {/* Badge */}
        <span className={`absolute top-0 right-0 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
          isBusinessRegime
            ? 'bg-emerald-500/20 text-emerald-400'
            : (driverState?.primary_layer === 'LAW' || complianceStatus === 'BEYOND_LAW' 
                ? 'bg-red-500/20 text-red-400' 
                : 'bg-amber-500/20 text-amber-400')
        }`}>
          {isBusinessRegime ? 'BIZ' : (driverState?.primary_layer === 'LAW' || complianceStatus === 'BEYOND_LAW' ? 'LAW' : 'BIZ')}
        </span>
        
        <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
          {isUrgent ? <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" /> :
           isWarning ? <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" /> :
           <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />}
          <p className={`text-[10px] sm:text-xs ${
            isBusinessRegime 
              ? (driverState?.warning_active ? 'text-amber-400' : 'text-slate-400')
              : (isUrgent ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-slate-400')
          }`}>
            {isBusinessRegime ? 'Rest In' : 'Rest By'}
          </p>
        </div>
        <p className={`text-lg sm:text-xl font-bold ${
          isBusinessRegime
            ? (driverState?.warning_active ? 'text-amber-400' : 'text-white')
            : (isUrgent ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-white')
        }`}>
          {isBusinessRegime ? (
            formatTime(Math.max(0, driverState?.time_until_rest_required_sec || 0))
          ) : (
            (() => {
              const baseRestBy = driverState?.need_rest_by_ts;
              if (!baseRestBy || !currentSegment?.start_ts) {
                return formatAbsoluteTime(baseRestBy);
              }
              const startTs = new Date(currentSegment.start_ts);
              const maxWork = driverState?.primary_layer === 'SHADOW' ? 17700 : 18900;
              const restByTime = new Date(startTs.getTime() + maxWork * 1000);
              return formatAbsoluteTime(restByTime.toISOString());
            })()
          )}
        </p>
      </div>
    </div>
  );
};

// Action Buttons Component
export const ActionButtons = ({ isWorking, isResting, onStartWork, onStartRest }) => (
  <div className="fixed bottom-0 left-0 right-0 bg-slate-800/95 backdrop-blur-lg border-t border-slate-700 p-3 sm:p-4 safe-area-bottom z-30">
    <div className="grid grid-cols-2 gap-2 sm:gap-3">
      <button
        onClick={onStartWork}
        className={`flex items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 rounded-xl font-semibold transition text-sm sm:text-base ${
          isWorking 
            ? 'bg-blue-500 text-white' 
            : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/50'
        }`}
        data-testid="start-work-btn"
      >
        <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
        {isWorking ? 'Working' : 'Start Work'}
      </button>
      
      <button
        onClick={onStartRest}
        className={`flex items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 rounded-xl font-semibold transition text-sm sm:text-base ${
          isResting 
            ? 'bg-emerald-500 text-white' 
            : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/50'
        }`}
        data-testid="start-rest-btn"
      >
        <Coffee className="w-4 h-4 sm:w-5 sm:h-5" />
        {isResting ? 'Resting' : 'Start Rest'}
      </button>
    </div>
  </div>
);

// Quick Action Row Component  
export const QuickActionRow = ({ onShowJourney, onShowFinish }) => (
  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3 sm:mt-4">
    <button
      onClick={onShowJourney}
      className="flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3 bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl font-medium transition border border-slate-600 text-sm sm:text-base"
      data-testid="distance-check-btn"
    >
      <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
      Distance Check
    </button>
    
    <button
      onClick={onShowFinish}
      className="flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3 bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl font-medium transition border border-slate-600 text-sm sm:text-base"
      data-testid="end-day-btn"
    >
      <Moon className="w-4 h-4 sm:w-5 sm:h-5" />
      End Day
    </button>
  </div>
);
